import React, { useState } from 'react'

const CURRENT_STATUS_OPTIONS = [
  'Vehicle Not Found',
  'Vehicle Not Fit for Inspection',
  'Ready for Delivery & Customer Validation Pending',
]

const PARTS_CONSUMED_OPTIONS = [
  'DIU',
  'Master Dock',
  'Master Dock Chugori',
  'Slave Dock',
  'Slave Dock Chugori',
]

const TICKET_STATUS_OPTIONS = [
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed',  label: 'Closed'  },
]

const emptyForm = (resolvedData) => {
  const rf = resolvedData?.resolution_fields || {}
  return {
    current_status:  rf.current_status  || '',
    parts_consumed:  rf.parts_consumed  || '',
    dock_diu_id:     rf.dock_diu_id     || '',
    remarks:         rf.remarks         || '',
    ticket_status:   resolvedData?.ticket_status  || '',
    issue_resolved:  resolvedData?.issue_resolved || '',
  }
}

export default function AfterSalesCloseForm({ resolvedData, readOnly, onSubmit, submitting }) {
  const [form, setForm] = useState(() => emptyForm(resolvedData))
  const [errors, setErrors] = useState({})

  const isClosed = form.ticket_status === 'closed'

  function set(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'ticket_status' && value !== 'closed') next.issue_resolved = ''
      return next
    })
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  function validate() {
    const e = {}
    if (!form.current_status)           e.current_status  = 'Required'
    if (!form.ticket_status)            e.ticket_status   = 'Required'
    if (isClosed && !form.issue_resolved) e.issue_resolved = 'Required'
    return e
  }

  function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    const payload = {
      resolution_fields: {
        current_status: form.current_status,
        parts_consumed: form.parts_consumed,
        dock_diu_id:    form.dock_diu_id,
        remarks:        form.remarks,
      },
      ticket_status: form.ticket_status,
      ...(isClosed && { issue_resolved: form.issue_resolved }),
    }
    onSubmit(payload)
  }

  return (
    <form className="ctf-section" onSubmit={handleSubmit} noValidate>

      {readOnly && (
        <div className="ctf-readonly-banner">
          &#128274; This ticket is closed — view only
        </div>
      )}

      {/* Current Status */}
      <Field label="Current Status" required={!readOnly} error={errors.current_status}>
        <select
          className="ctf-select"
          value={form.current_status}
          onChange={(e) => set('current_status', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select status</option>
          {CURRENT_STATUS_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </Field>

      {/* Parts Consumed */}
      <Field label="Parts Consumed" error={errors.parts_consumed}>
        <select
          className="ctf-select"
          value={form.parts_consumed}
          onChange={(e) => set('parts_consumed', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select part</option>
          {PARTS_CONSUMED_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </Field>

      {/* Dock DIU ID */}
      <Field label="Dock DIU ID" error={errors.dock_diu_id}>
        <input
          className="ctf-input"
          value={form.dock_diu_id}
          onChange={(e) => set('dock_diu_id', e.target.value)}
          placeholder="Enter Dock / DIU ID"
          readOnly={readOnly}
        />
      </Field>

      {/* Remarks */}
      <Field label="Remarks" error={errors.remarks}>
        <textarea
          className="ctf-textarea"
          value={form.remarks}
          onChange={(e) => set('remarks', e.target.value)}
          placeholder="Add any remarks…"
          readOnly={readOnly}
        />
      </Field>

      {/* ── Divider before status fields ── */}
      <div className="ctf-divider" />

      {/* Ticket Status */}
      <Field label="Ticket Status" required={!readOnly} error={errors.ticket_status}>
        <select
          className="ctf-select"
          value={form.ticket_status}
          onChange={(e) => set('ticket_status', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select status</option>
          {TICKET_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      {/* Issue Resolved — only active when ticket_status is closed */}
      <Field
        label="Issue Resolved"
        required={isClosed && !readOnly}
        error={errors.issue_resolved}
        hint={!isClosed && !readOnly ? 'Available only when Ticket Status is Closed' : undefined}
      >
        <div className={`ctf-radio-group ${(!isClosed || readOnly) ? 'ctf-radio-group--disabled' : ''}`}>
          {['yes', 'no'].map((v) => (
            <label key={v} className="ctf-radio-label">
              <input
                type="radio"
                name="issue_resolved"
                value={v}
                checked={form.issue_resolved === v}
                disabled={!isClosed || readOnly}
                onChange={() => set('issue_resolved', v)}
                className="ctf-radio"
              />
              {v === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      </Field>

      {!readOnly && (
        <button type="submit" className="ctf-submit-btn" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Update Ticket'}
        </button>
      )}
    </form>
  )
}

function Field({ label, required, error, hint, children }) {
  return (
    <div className="ctf-field">
      <span className="ctf-label">
        {label}
        {required && <span className="ctf-required">*</span>}
      </span>
      {children}
      {hint  && !error && <span className="ctf-hint">{hint}</span>}
      {error && <span className="ctf-field-error">{error}</span>}
    </div>
  )
}
